import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import VisibilitySensor from 'react-visibility-sensor';
import Button from '../../components/Button/Button';
import { FiltersEnum, SORT_ENUM } from '../../components/Filters/Filters.helpers';
import Grid from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import NoResults from '../../components/NoResults/NoResults';
import Page from '../../components/Page/Page';
import PdfReader from '../../components/PdfReader/PdfReader';
import Ribbon from '../../components/Ribbon/Ribbon';
import Slider from '../../components/Slider/Slider';
import useContentRibbons from '../../core/apiHooks/useContentRibbons';
import { useDocumentsWithFilters } from '../../core/apiHooks/useDocumentsWithFilters';
import useFeaturedPublications from '../../core/apiHooks/useFeaturedPublications';
import useScrollToResultsOnLoad from '../../core/hooks/useScrollToResultsOnLoad';
import { DocumentTypesEnum } from '../../core/enums/PublicationTypesEnum';
import { getSlidesPerView } from '../../core/formatters';
import { ContentRibbon } from '../../core/models/ContentRibbon';
import { iTag } from '../../core/models/Tag';
import Routes from '../../core/Routes';
import { CountriesEnum, GlobalContext } from '../../GlobalContext';
import { IconArrowRight, IconInfo } from '../../icons';
import FeaturedContent from '../__Common/FeaturedContent/FeaturedContent';
import './Library.styles.scss';
import BrowseSection from './BrowseSection/BrowseSection';
import StickyBrowseSection from '../../components/DataTable/StickyBrowseSection/StickyBrowseSection';
import Table from './Table/Table';

export const DEFAULT_TYPES: DocumentTypesEnum[] = [DocumentTypesEnum.Publication];

const Library: React.FC = () => {
    const {
        inMyLibrary,
        selectedCountryId,
        currentUser: { globalSubscriptionPackageIds }
    } = useContext(GlobalContext);
    const { data, isLoading } = useContentRibbons(inMyLibrary);
    const { data: featuredPublications, isLoading: isFeaturedContentLoading } = useFeaturedPublications();
    const location = useLocation();
    const navigate = useNavigate();
    const route = Routes.find(({ path }) => path === location.pathname) || Routes[0];
    const title = route?.title || '';

    const [isMacInfoPopupOpened, setIsMacInfoPopupOpened] = useState(false);
    const [isViewChanging, setIsViewChanging] = useState(false);
    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const savedMode = localStorage.getItem('browseSection:viewMode');
        return savedMode === 'tile' || savedMode === 'table' ? savedMode : 'tile';
    });
    const [isNewRelease, setIsNewRelease] = useState(() => {
        const savedNewRelease = localStorage.getItem('library:isNewRelease');
        return savedNewRelease === 'true';
    });
    const [__isSorting, setIsSorting] = useState(false);

    const handleViewModeChange = (mode: 'tile' | 'table') => {
        if (mode !== viewMode) {
            setIsViewChanging(true);
            setViewMode(mode);
            setTimeout(() => setIsViewChanging(false), 300);
        }
    };

    const [urlFilters, setUrlFilters] = useQueryStates(
        {
            ribbonsIds: parseAsArrayOf(parseAsInteger).withDefault([]),
            tagsIds: parseAsArrayOf(parseAsInteger).withDefault([]),
            subscriptionPackageIds: parseAsArrayOf(parseAsInteger).withDefault([]),
            types: parseAsArrayOf(parseAsString).withDefault([DocumentTypesEnum.Publication]),
            orderBy: parseAsString.withDefault(SORT_ENUM.DATE),
            sortOrder: parseAsString.withDefault(SORT_ENUM.DESC),
            searchQuery: parseAsString.withDefault('')
        },
        {
            urlKeys: {
                ribbonsIds: FiltersEnum.ribbonsIds,
                tagsIds: FiltersEnum.tags,
                subscriptionPackageIds: FiltersEnum.packages,
                types: FiltersEnum.types,
                orderBy: FiltersEnum.orderBy,
                sortOrder: FiltersEnum.sortOrder,
                searchQuery: FiltersEnum.searchText
            },
            history: 'replace'
        }
    );

    const [categoriesIds, setCategoriesIds] = useState<number[]>([]);
    const [packageIds, setPackageIds] = useState<number[]>([]);
    const [hasAccess, setHasAccess] = useState<boolean>(inMyLibrary);

    useEffect(() => {
        localStorage.setItem('library:isNewRelease', isNewRelease.toString());
    }, [isNewRelease]);

    const browseFilters = useMemo(() => {
        const ribbonsWithoutSentinel = urlFilters.ribbonsIds.filter((id): id is number => id !== 18);
        const ribbonsIds = isNewRelease ? [...ribbonsWithoutSentinel, 18] : ribbonsWithoutSentinel;
        return {
            ribbonsIds,
            categoriesIds,
            subscriptionPackageIds: urlFilters.subscriptionPackageIds,
            tagsIds: urlFilters.tagsIds,
            orderBy: urlFilters.orderBy,
            sortOrder: urlFilters.sortOrder,
            hasAccess,
            types: urlFilters.types as DocumentTypesEnum[],
            searchQuery: urlFilters.searchQuery,
            packageIds
        };
    }, [urlFilters, categoriesIds, packageIds, hasAccess, isNewRelease]);

    const { wWidth, getRem } = useContext(GlobalContext);
    const tilesPerView = getSlidesPerView(wWidth - getRem(2 * 48), 210);
    const take = 5 * tilesPerView;

    const preSelectedSubscriptionPackages = useMemo(() => {
        if (selectedCountryId === CountriesEnum.Global) {
            return globalSubscriptionPackageIds;
        }
        return [];
    }, [selectedCountryId, globalSubscriptionPackageIds]);

    const typesForApi = useMemo(() => {
        if (selectedCountryId !== CountriesEnum.Global) return browseFilters.types;
        const filtered = browseFilters.types.filter((t) => t !== DocumentTypesEnum.Mac);
        return filtered.length > 0 ? filtered : DEFAULT_TYPES;
    }, [selectedCountryId, browseFilters.types]);

    const {
        isLoading: isGridLoading,
        data: gridDocsFromApi,
        hasNextItems,
        incSkip
    } = useDocumentsWithFilters(
        {
            ribbonsIds: browseFilters.ribbonsIds.filter((id) => id !== 18),
            categoriesIds: browseFilters.categoriesIds,
            includeAnySubscriptionPackageIds: browseFilters.subscriptionPackageIds,
            includeAllSubscriptionPackageIds: preSelectedSubscriptionPackages,
            includeAnyTagsIds: browseFilters.tagsIds,
            includeAllTagsIds: [],
            orderBy: browseFilters.orderBy,
            sortOrder: browseFilters.sortOrder,
            hasAccess: browseFilters.hasAccess,
            types: typesForApi,
            searchText: browseFilters.searchQuery
        },
        take
    );

    const gridDocs = gridDocsFromApi;

    const ribbons = data;

    type BrowseFilters = typeof browseFilters;

    const updateBrowseFilters = (patch: Partial<BrowseFilters>) => {
        if (
            (patch.orderBy !== undefined && patch.orderBy !== browseFilters.orderBy) ||
            (patch.sortOrder !== undefined && patch.sortOrder !== browseFilters.sortOrder)
        ) {
            setIsSorting(true);
            setTimeout(() => setIsSorting(false), 300); // Brief loading state
        }

        let nextSortOrder = patch.sortOrder;
        if (patch.ribbonsIds?.includes(18)) {
            setIsNewRelease(true);
            if (!patch.sortOrder && !isNewRelease) {
                nextSortOrder = SORT_ENUM.DESC;
            }
        } else if (patch.ribbonsIds !== undefined && !patch.ribbonsIds.includes(18)) {
            setIsNewRelease(false);
        }

        const urlPatch: Partial<{
            ribbonsIds: number[];
            tagsIds: number[];
            subscriptionPackageIds: number[];
            types: string[];
            orderBy: string;
            sortOrder: string;
            searchQuery: string;
        }> = {};

        if (patch.ribbonsIds !== undefined) {
            urlPatch.ribbonsIds = patch.ribbonsIds.filter((id) => id !== 18);
        }
        if (patch.tagsIds !== undefined) urlPatch.tagsIds = patch.tagsIds;
        if (patch.subscriptionPackageIds !== undefined) urlPatch.subscriptionPackageIds = patch.subscriptionPackageIds;
        if (patch.types !== undefined) urlPatch.types = patch.types;
        if (patch.orderBy !== undefined) urlPatch.orderBy = patch.orderBy;
        if (nextSortOrder !== undefined) urlPatch.sortOrder = nextSortOrder;
        if (patch.searchQuery !== undefined) urlPatch.searchQuery = patch.searchQuery;

        if (Object.keys(urlPatch).length > 0) {
            setUrlFilters(urlPatch);
        }

        if (patch.categoriesIds !== undefined) setCategoriesIds(patch.categoriesIds);
        if (patch.packageIds !== undefined) setPackageIds(patch.packageIds);
        if (patch.hasAccess !== undefined) setHasAccess(patch.hasAccess);
    };

    const hasTypeFilter = useMemo(() => {
        return (
            browseFilters.types.length !== DEFAULT_TYPES.length || !DEFAULT_TYPES.every((t) => browseFilters.types.includes(t))
        );
    }, [browseFilters.types]);

    // Opened with a filter already in the URL (e.g. a tag link from a publication): bring the results into view
    // once the featured block above them has loaded. View mode and country are not URL filters, so they don't count.
    const browseWrapperRef = useRef<HTMLDivElement>(null);
    useScrollToResultsOnLoad(browseWrapperRef, {
        enabled:
            urlFilters.tagsIds.length > 0 ||
            urlFilters.subscriptionPackageIds.length > 0 ||
            urlFilters.ribbonsIds.length > 0 ||
            urlFilters.searchQuery !== '' ||
            hasTypeFilter,
        isReady: !isLoading && !isFeaturedContentLoading
    });

    const shouldShowFilteredResults =
        selectedCountryId === CountriesEnum.Global ||
        viewMode === 'table' ||
        browseFilters.ribbonsIds.length > 0 ||
        browseFilters.tagsIds.length > 0 ||
        browseFilters.subscriptionPackageIds.length > 0 ||
        isNewRelease ||
        hasTypeFilter;

    const handleTagClick = (tag: iTag) => {
        updateBrowseFilters({ tagsIds: [tag.id], subscriptionPackageIds: [] });
    };

    // Sync hasAccess filter with the global inMyLibrary toggle
    useEffect(() => {
        if (hasAccess !== inMyLibrary) {
            setHasAccess(inMyLibrary);
        }
    }, [inMyLibrary, hasAccess]);

    return (
        <Page title={title} isShownStickyFooter={!!ribbons.length}>
            <div className="Library">
                {selectedCountryId === CountriesEnum.Usa && (
                    <>
                        {!isFeaturedContentLoading && featuredPublications.length > 0 && (
                            <div className="Library__section is-featured">
                                <Slider>
                                    {featuredPublications.map((p) => (
                                        <FeaturedContent key={p.id} data={p} />
                                    ))}
                                </Slider>
                            </div>
                        )}
                        {isFeaturedContentLoading && (
                            <div className="Library__section">
                                <Loader />
                            </div>
                        )}
                    </>
                )}

                <div className="Library__section">
                    {isLoading && !isFeaturedContentLoading && <Loader />}
                    {!isLoading && (
                        <>
                            <div ref={browseWrapperRef} className={`Library__browse-wrapper`}>
                                <StickyBrowseSection
                                    BrowseSectionComponent={BrowseSection}
                                    componentProps={{
                                        shouldShowFilteredResults,
                                        viewMode,
                                        onViewModeChange: handleViewModeChange,
                                        filters: browseFilters,
                                        onChange: (filters) => {
                                            updateBrowseFilters(filters as Partial<BrowseFilters>);
                                        }
                                    }}
                                    customStyles={{
                                        ...(viewMode === 'tile'
                                            ? {
                                                  left: '-1px',
                                                  width: '100%',
                                                  padding: '0 50px'
                                              }
                                            : {})
                                    }}
                                />
                            </div>

                            {shouldShowFilteredResults && (
                                <div className="Library__results">
                                    {isViewChanging && (
                                        <div className="Library__view-loader">
                                            <Loader />
                                        </div>
                                    )}

                                    {!isViewChanging && viewMode === 'tile' ? (
                                        <>
                                            <Grid
                                                data={new ContentRibbon({ clientModel: { items: gridDocs } })}
                                                columnsNumber={tilesPerView}
                                            />

                                            {!gridDocs.length && !isGridLoading && (
                                                <NoResults title="No Results found" subTitle="" />
                                            )}
                                        </>
                                    ) : (
                                        !isViewChanging && (
                                            <div
                                                className={`Library__table-container ${
                                                    !gridDocs.length ? 'has-no-results' : ''
                                                } ${isGridLoading ? 'is-loading' : ''}`}
                                            >
                                                <Table
                                                    data={gridDocs}
                                                    onRowClick={(publication) => {
                                                        if (publication.customLink && publication.hasAccess) {
                                                            if (/^https?:\/\//.test(publication.customLink)) {
                                                                window.open(publication.customLink, '_blank');
                                                            } else {
                                                                navigate(publication.customLink);
                                                            }
                                                        } else {
                                                            navigate(`/details/${publication.id}`);
                                                        }
                                                    }}
                                                    onTagClick={
                                                        (browseFilters.types.length === 1 &&
                                                            browseFilters.types[0] === DocumentTypesEnum.Mac) ||
                                                        browseFilters.ribbonsIds.includes(19)
                                                            ? undefined
                                                            : handleTagClick
                                                    }
                                                    isLoading={isGridLoading}
                                                    searchQuery={browseFilters.searchQuery}
                                                    orderBy={browseFilters.orderBy}
                                                    sortOrder={browseFilters.sortOrder}
                                                    onSort={(orderBy, sortOrder) => {
                                                        updateBrowseFilters({ orderBy, sortOrder });
                                                    }}
                                                    typeFilter={typesForApi}
                                                    onTypeFilterChange={(types) => {
                                                        // If only MAC is selected, clear tags and set orderBy to updateDate
                                                        if (types.length === 1 && types[0] === DocumentTypesEnum.Mac) {
                                                            updateBrowseFilters({
                                                                types,
                                                                tagsIds: [],
                                                                subscriptionPackageIds: [],
                                                                orderBy: 'updateDate'
                                                            });
                                                        } else {
                                                            updateBrowseFilters({
                                                                types,
                                                                orderBy: SORT_ENUM.DATE // Reset to default date sort
                                                            });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )
                                    )}

                                    {viewMode === 'tile' && isGridLoading && !isViewChanging && (
                                        <div className={`Browse__loader ${gridDocs.length === 0 ? 'is-initial' : ''}`}>
                                            <Loader />
                                        </div>
                                    )}

                                    <VisibilitySensor
                                        onChange={(visible) => {
                                            if (visible && hasNextItems && !isGridLoading) incSkip();
                                        }}
                                        partialVisibility
                                    >
                                        {!isGridLoading ? <div className="Browse__sensor" /> : <div />}
                                    </VisibilitySensor>
                                </div>
                            )}

                            {!shouldShowFilteredResults && (
                                <>
                                    {isViewChanging && (
                                        <div className="Library__results">
                                            <div className="Library__view-loader">
                                                <Loader />
                                            </div>
                                        </div>
                                    )}

                                    {!isViewChanging &&
                                        ribbons.map((ribbon) => {
                                            const isMacRibbon = ribbon.isMacRibbon;
                                            const ribbonTitle = (
                                                <>
                                                    {ribbon.title}
                                                    {isMacRibbon && (
                                                        <>
                                                            <Button
                                                                className="Library__title-icon"
                                                                onClick={() => setIsMacInfoPopupOpened(true)}
                                                            >
                                                                <IconInfo />
                                                            </Button>
                                                            <PdfReader
                                                                isOpened={isMacInfoPopupOpened}
                                                                close={() => setIsMacInfoPopupOpened(false)}
                                                                title="MAC Diagram"
                                                                fileUrl={'/MAC_DIAGRAM.pdf'}
                                                            />
                                                        </>
                                                    )}
                                                    <Button
                                                        className="ShowAllButton"
                                                        onClick={() => {
                                                            updateBrowseFilters({
                                                                ribbonsIds: [ribbon.id],
                                                                ...(isMacRibbon ? { types: [DocumentTypesEnum.Mac] } : {})
                                                            });
                                                        }}
                                                    >
                                                        Show all
                                                        <IconArrowRight
                                                            style={{
                                                                transform: 'scale(0.8) translate(-30%, 3%)'
                                                            }}
                                                        />
                                                    </Button>
                                                </>
                                            );

                                            return <Ribbon key={ribbon.id} data={{ ...ribbon, title: ribbonTitle }} />;
                                        })}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Page>
    );
};

export default Library;
